import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    effect,
    inject,
    input,
    output,
    signal,
    untracked,
} from '@angular/core';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonComponent } from 'src/app/@core/components/ui/button/button.component';
import { IconComponent } from 'src/app/@core/components/ui/icon/icon.component';
import { IUser } from 'src/app/@core/models/model';
import { UserNameDisplayComponent } from 'src/app/@shared/components/user-name-display/user-name-display.component';
import { PromptAccessLevelEnum } from 'src/app/features/general-agent/services/prompt-library.service';
import { PromptReuseCandidate, PromptReuseCandidateCreator, ThreadMessagesService } from '../../../services/thread-messages.service';
import {
    AUTO_APPROVE_SECONDS,
    clearCountdown,
    formatCountdown,
    readCountdown,
    writeCountdown,
} from '../../../utils/interrupt-countdown';

/**
 * PromptReuseCardComponent
 *
 * Displayed when PromptEnhancerMiddleware fires a PROMPT_REUSE_SUGGESTION interrupt.
 * Shows up to 3 semantically-similar saved prompts so the user can:
 *  - Click "Use this" to inject one as the approved brief (skips enhancement).
 *  - Click "Generate new prompt" to proceed with the normal enhancement flow.
 *
 * Outputs:
 *  - ``useExisting``    — user selected an existing prompt; carries its promptId.
 *  - ``generateNew``    — user wants a freshly generated enhancement instead.
 */
@Component({
    selector: 'app-prompt-reuse-card',
    standalone: true,
    imports: [CommonModule, ButtonComponent, IconComponent, UserNameDisplayComponent, TooltipModule],
    templateUrl: './prompt-reuse-card.component.html',
    styleUrl: './prompt-reuse-card.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PromptReuseCardComponent implements OnInit, OnDestroy {
    // ── Inputs ───────────────────────────────────────────────────────────────
    /** List of similar saved-prompt candidates to offer the user. */
    candidates = input.required<PromptReuseCandidate[]>();

    // ── Outputs ──────────────────────────────────────────────────────────────
    /** User chose to reuse the prompt with this ID. */
    useExisting = output<string>();

    /** User wants a new AI-generated enhancement instead. */
    generateNew = output<void>();

    // ── Local state ──────────────────────────────────────────────────────────
    /** Track which candidate's content is expanded for preview. */
    expandedPromptId = signal<string | null>(null);

    /** ID of the candidate currently being acted on (prevents double-clicks). */
    loadingPromptId = signal<string | null>(null);

    /** Whether the "Generate new" button is in a loading state. */
    isGenerating = signal(false);

    /** Seconds remaining before auto-selecting the top-ranked prompt. */
    timeRemaining = signal<number>(AUTO_APPROVE_SECONDS);

    private _timerInterval?: ReturnType<typeof setInterval>;

    private threadMessagesService = inject(ThreadMessagesService);

    /** Thread the countdown deadline is persisted against. */
    private get _threadId(): string {
        return this.threadMessagesService.activeThreadId();
    }

    // ── Lifecycle ─────────────────────────────────────────────────────────────
    ngOnInit(): void {
        // Resume the deadline this thread was already counting down to, so a
        // browser refresh doesn't hand the user a fresh two minutes.
        const restored = readCountdown(this._threadId);
        if (restored === 0) {
            this._onTimerExpired();
            return;
        }

        const seconds = restored ?? AUTO_APPROVE_SECONDS;
        this.timeRemaining.set(seconds);
        writeCountdown(this._threadId, seconds);

        this._timerInterval = setInterval(() => {
            const remaining = this.timeRemaining();
            if (remaining > 0) {
                this.timeRemaining.set(remaining - 1);
            } else {
                this._onTimerExpired();
            }
        }, 1000);
    }

    ngOnDestroy(): void {
        this._clearTimer();
    }

    private _clearTimer(): void {
        if (this._timerInterval !== undefined) {
            clearInterval(this._timerInterval);
            this._timerInterval = undefined;
        }
    }

    /**
     * Stop the countdown and forget its deadline — the interrupt is resolved.
     * Deliberately not called from ``ngOnDestroy``: a refresh tears the card
     * down, and that is precisely when the deadline must survive.
     */
    private _endTimer(): void {
        this._clearTimer();
        clearCountdown(this._threadId);
    }

    private _onTimerExpired(): void {
        this._endTimer();
        const top = this.candidates()[0];
        if (top) {
            this.onUseExisting(top.promptId);
        }
    }

    formatTime(seconds: number): string {
        return formatCountdown(seconds);
    }

    // ── Auto-expand first candidate ──────────────────────────────────────────
    private readonly _defaultExpandEffect = effect(() => {
        const cands = this.candidates();
        if (cands.length > 0) {
            untracked(() => {
                if (this.expandedPromptId() === null) {
                    this.expandedPromptId.set(cands[0].promptId);
                }
            });
        }
    });

    // ── Expose enum to template ───────────────────────────────────────────────
    /** Allows the template to reference PromptAccessLevelEnum values directly. */
    protected readonly AccessLevel = PromptAccessLevelEnum;

    // ── Helpers ──────────────────────────────────────────────────────────────

    /** Toggle expanded preview for a candidate. */
    toggleExpand(promptId: string): void {
        this.expandedPromptId.update(id => id === promptId ? null : promptId);
    }

    /** Format a 0–1 cosine score as a rounded integer percentage. */
    scorePercent(score: number): number {
        return Math.round(score * 100);
    }

    /**
     * Returns the stroke colour for the circular score ring based on similarity threshold:
     *  - Green  (#10b981) → score > 80 %  (strong match)
     *  - Yellow (#f59e0b) → score 50–80 % (moderate match)
     *  - Gray   (#9ca3af) → score < 50 %  (weak match)
     */
    scoreColor(score: number): string {
        const pct = this.scorePercent(score);
        if (pct > 80) return '#10b981';   // green  — high similarity
        if (pct >= 50) return '#f59e0b';  // yellow — medium similarity
        return '#9ca3af';                  // gray   — low similarity
    }

    /**
     * SVG stroke-dasharray for the circular score ring.
     * Uses a virtual circumference of 100 so the value equals the percentage.
     */
    scoreDasharray(score: number): string {
        const pct = this.scorePercent(score);
        return `${pct} ${100 - pct}`;
    }

    /** Whether this candidate is the top-ranked pick (index 0). */
    isTopPick(index: number): boolean {
        return index === 0;
    }

    /** Human-readable relative time ("3d ago", "1w ago", "just now"). */
    timeAgo(isoDate: string | null | undefined): string {
        if (!isoDate) return '';
        const diff = Date.now() - new Date(isoDate).getTime();
        const mins = Math.floor(diff / 60_000);
        if (mins < 1) return 'just now';
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        const days = Math.floor(hrs / 24);
        if (days < 7) return `${days}d ago`;
        const weeks = Math.floor(days / 7);
        if (weeks < 5) return `${weeks}w ago`;
        const months = Math.floor(days / 30);
        return `${months}mo ago`;
    }

    /** Convert a PromptReuseCandidateCreator to Partial<IUser> (strips nulls). */
    creatorAsUser(creator: PromptReuseCandidateCreator | null | undefined): Partial<IUser> {
        if (!creator) return {};
        return {
            fullName: creator.fullName ?? undefined,
            firstName: creator.firstName ?? undefined,
            lastName: creator.lastName ?? undefined,
            email: creator.email ?? undefined,
        };
    }

    /** Label for the access-level chip ("team" or "personal"). */
    accessLabel(level: string | undefined): string {
        return (level === 'WORKSPACE') ? 'team' : 'personal';
    }

    /**
     * Comma-separated group names used for the folder-badge tooltip.
     * Returns an empty string when the candidate has no groups, which hides the badge.
     */
    groupsLabel(candidate: PromptReuseCandidate): string {
        const groups = candidate.groups;
        if (!groups || groups.length === 0) return '';
        return groups.join(',\n ');
    }

    // ── Action handlers ───────────────────────────────────────────────────────

    onUseExisting(promptId: string): void {
        if (this.loadingPromptId() !== null || this.isGenerating()) return;
        this._endTimer();
        this.loadingPromptId.set(promptId);
        this.useExisting.emit(promptId);
    }

    onGenerateNew(): void {
        if (this.loadingPromptId() !== null || this.isGenerating()) return;
        // Clears the deadline so the enhancement card that follows starts fresh.
        this._endTimer();
        this.isGenerating.set(true);
        this.generateNew.emit();
    }
}
